import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ArrowUpRight, Globe } from "lucide-react";
import { useApi, img } from "@/lib/api";
import { Seo } from "@/components/site/Seo";
import { PageHeader, Reveal } from "@/components/site/Reveal";
import { FilterPills } from "@/pages/HomesForSale";
import { useLang } from "@/lib/i18n";

export const DirectoryCard = ({ d, highlight = false }) => {
  const { t, L } = useLang();
  return (
  <article id={`dir-${d.id}`} className={`card-lift flex h-full scroll-mt-32 flex-col overflow-hidden rounded-2xl border bg-white transition-shadow duration-700 ${highlight ? "border-forest shadow-[0_0_0_4px_rgba(47,74,58,0.35)]" : "border-clay"}`} data-testid={`directory-card-${d.id || d.name}`}>
    <div className="img-zoom aspect-[4/3] overflow-hidden">
      <img src={img(d.image)} alt={`${L(d, "name")} — Escamequita`} loading="lazy" className="h-full w-full object-cover" />
    </div>
    <div className="flex flex-1 flex-col p-6">
      <p className="eyebrow">{L(d, "category")}</p>
      <h3 className="mt-2 font-display text-2xl font-black text-ink">{L(d, "name")}</h3>
      <p className="mt-3 text-sm font-medium leading-relaxed text-moss">{L(d, "description")}</p>
      <div className="mt-auto flex flex-wrap items-center gap-4 pt-6">
        {d.cta_url && (
          <a href={d.cta_url} target="_blank" rel="noreferrer" className="btn-primary !px-5 !py-2.5" data-testid={`directory-cta-${d.id}`}>
            {L(d, "cta_label") || t("dir.contact")} <ArrowUpRight className="h-4 w-4" />
          </a>
        )}
        {d.website_url && (
          <a href={d.website_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm font-bold text-forest hover:underline" data-testid={`directory-website-${d.id}`}>
            <Globe className="h-4 w-4" /> {t("dir.website")}
          </a>
        )}
      </div>
    </div>
  </article>
  );
};

export default function Directory() {
  const { t, L } = useLang();
  const { data } = useApi("/directory", []);
  const [cat, setCat] = useState("all");
  const cats = [{ id: "all", label: t("filter.all") }, ...[...new Map(data.filter((d) => d.category).map((d) => [d.category, L(d, "category")])).entries()].map(([id, label]) => ({ id, label }))];
  const items = cat === "all" ? data : data.filter((d) => d.category === cat);
  const { hash } = useLocation();
  const [hl, setHl] = useState(null);
  useEffect(() => {
    const id = hash.startsWith("#dir-") ? hash.slice(5) : null;
    if (!id || !data.length) return undefined;
    const t1 = setTimeout(() => {
      document.getElementById(`dir-${id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      setHl(id);
    }, 400);
    const t2 = setTimeout(() => setHl(null), 3200);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [hash, data.length]);
  return (
    <>
      <Seo title={t("dir.seoTitle")} description={t("dir.seoDesc")} />
      <PageHeader testId="directory-header" eyebrow={t("dir.eyebrow")} title={t("dir.title")}>
        {t("dir.intro")}
      </PageHeader>
      <section className="container-x pb-28">
        <FilterPills prefix="directory" options={cats} value={cat} onChange={setCat} />
        <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-3" data-testid="directory-grid">
          {items.map((d, i) => (
            <Reveal key={d.id} delay={(i % 3) * 0.08}><DirectoryCard d={d} highlight={hl === d.id} /></Reveal>
          ))}
        </div>
      </section>
    </>
  );
}
